import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { generateProjectId } from '../../utils/idGenerator';

const ProjectNameModal = ({ onSubmit, onCancel }) => {
  const [projectName, setProjectName] = useState('');
  const [creationMethod, setCreationMethod] = useState(null); // 'ai' or 'manual'
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    
    if (projectName.trim().length < 3) {
      setError('Project name must be at least 3 characters');
      return;
    }
    
    if (!creationMethod) {
      setError('Please select a creation method');
      return;
    }
    
    const projectId = generateProjectId();
    
    onSubmit(projectName.trim(), projectId, creationMethod);
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center z-[1000] p-6 animate-fadeIn bg-bg-primary/70 backdrop-blur-sm">
      <div className="w-full max-w-[600px] rounded-xl shadow-2xl overflow-hidden animate-slideUp max-h-[90vh] overflow-y-auto bg-bg-secondary/95 border border-primary-500/15 shadow-[0_25px_50px_rgba(3,6,4,0.5),0_0_40px_rgba(50,205,50,0.1)]">
        <div className="py-6 px-6 pb-4 sticky top-0 z-10 border-b border-primary-500/10 bg-gradient-to-b from-primary-500/5 to-transparent">
          <h2 className="text-xl font-semibold m-0 mb-1 text-text-primary">Create New Prompt</h2>
          <p className="text-sm m-0 leading-relaxed text-text-tertiary">Choose how to create your prompt</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Prompt Name */}
          <div className="mb-5">
            <label htmlFor="projectName" className="flex items-center gap-1 text-sm font-medium mb-2 text-text-secondary">
              Prompt Name
              <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                setError('');
              }}
              placeholder="e.g., Customer Support Bot"
              className={`w-full p-3 text-base rounded-md transition-all duration-200 bg-bg-input/80 text-text-primary focus:outline-none focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/15 focus:shadow-[0_0_20px_rgba(50,205,50,0.1)] ${
                error ? 'border border-error ring-[3px] ring-error/15' : 'border border-primary-500/15'
              }`}
              autoFocus
            />
          </div>

          {/* Creation Method Selection */}
          <div className="mb-5">
            <label className="flex items-center gap-1 text-sm font-medium mb-3 text-text-secondary">
              Creation Method
              <span className="text-error">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setCreationMethod('ai');
                  setError('');
                }}
                className={`p-4 rounded-lg transition-all text-left ${
                  creationMethod === 'ai'
                    ? 'border-2 border-primary-500 bg-success-50 shadow-glow-sm'
                    : 'border-2 border-primary-500/15 bg-bg-secondary/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <svg className={`w-5 h-5 ${creationMethod === 'ai' ? 'text-primary-500' : 'text-text-tertiary'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                  </svg>
                  <div className={`font-medium ${creationMethod === 'ai' ? 'text-primary-500' : 'text-text-primary'}`}>AI-Assisted</div>
                </div>
                <div className="text-xs leading-relaxed text-text-tertiary">
                  Guided wizard with smart suggestions
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setCreationMethod('manual');
                  setError('');
                }}
                className={`p-4 rounded-lg transition-all text-left ${
                  creationMethod === 'manual'
                    ? 'border-2 border-primary-500 bg-success-50 shadow-glow-sm'
                    : 'border-2 border-primary-500/15 bg-bg-secondary/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <svg className={`w-5 h-5 ${creationMethod === 'manual' ? 'text-primary-500' : 'text-text-tertiary'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  <div className={`font-medium ${creationMethod === 'manual' ? 'text-primary-500' : 'text-text-primary'}`}>Manual Writing</div>
                </div>
                <div className="text-xs leading-relaxed text-text-tertiary">
                  Write prompts directly with full control
                </div>
              </button>
            </div>
          </div>

          {error && <p className="text-sm mb-4 text-error">{error}</p>}
          
          <div className="flex gap-3 justify-end">
            <button 
              type="button" 
              onClick={onCancel} 
              className="py-3 px-5 text-sm font-medium rounded-md cursor-pointer transition-all bg-bg-elevated/80 text-text-secondary border border-primary-500/20 hover:bg-success-50 hover:border-primary-500/30"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="py-3 px-5 text-sm font-medium rounded-md cursor-pointer transition-all bg-gradient-to-br from-primary-600 to-primary-500 text-black border-none shadow-glow-sm hover:from-primary-500 hover:to-green-400 hover:-translate-y-0.5 hover:shadow-glow"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ProjectNameModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ProjectNameModal;
